// filepath: apps/web/src/features/lists/EntityListTable.tsx
//
// FR-UI-LIST-1 AC-2 / AC-4 — presentational table for the list view.
//
// Pairs with useEntityList (data hook) and useEntityListFilter
// (URL-state hook). The component is presentational: it owns only
// the header-button sort-toggle UX (aria-sort wiring).
//
// INVARIANTS:
//   - INV-2 / FR-AUTH-7 / FR-UI-CORE-4 AC-1: the renderer NEVER
//     iterates `definition.fields` to infer column existence. It
//     renders exactly the columns the server returned. A field
//     absent from the response is not rendered; a field present in
//     the response but absent from the definition (server-added
//     after the definition snapshot) IS rendered — the server is
//     authoritative.
//   - INV-9: zero `dangerouslySetInnerHTML`. All server-supplied
//     values render as React text nodes.
//   - FR-UI-CORE-9 / WCAG 2.1 AA: sort-toggle is a real <button>
//     inside the <th>, with `aria-sort` carrying `ascending`,
//     `descending`, or `none`. Keyboard navigation works because
//     the buttons are native.
//   - AC-2: when the server returns an empty `records` envelope,
//     the table renders empty-state chrome (NOT a permission-denied
//     banner — that would leak entity-type existence per FR-QRY-3
//     AC-1 and the impact report §"FR-UI-CORE-10 5-states").
//   - The `aria-sort` attribute on the header <th> follows
//     WAI-ARIA 1.2: "ascending" | "descending" | "none" | "other".

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type {
  EntityDefinitionWire,
  EntityListPageEnvelope,
  EntityListSort,
} from "./types";

export interface EntityListTableProps {
  /** Resolved entity definition (used to derive column metadata). */
  definition: EntityDefinitionWire;
  /** Current page envelope from useEntityList. */
  page: EntityListPageEnvelope;
  /**
   * Active sort expressions. The header-button for each sort
   * column renders the corresponding `aria-sort` attribute.
   */
  sort: EntityListSort[];
  /** Called when the user clicks a column header sort-toggle. */
  onToggleSort: (field: string) => void;
  /**
   * Optional click-through to a record detail view. When supplied,
   * each row's record id becomes a clickable link. When absent,
   * rows render without links (read-only listings).
   */
  onRowClick?: (recordId: string) => void;
}

/**
 * Compute the set of columns the table will render. AC-4 invariant:
 * the columns come ONLY from fields actually present in at least one
 * record's `Fields` map. The server has already stripped any field
 * the caller may not read (INV-2 / FR-AUTH-7); the renderer MUST NOT
 * iterate `definition.fields` and render columns for fields absent
 * from the response — that would leak the existence of stripped
 * fields.
 *
 * The render order: declared-field order first (so the table column
 * order matches the authoring surface), followed by any record-only
 * keys (in first-seen order) so server-added fields still render.
 */
function resolveColumns(
  definition: EntityDefinitionWire,
  page: EntityListPageEnvelope,
): string[] {
  // First, gather the keys actually present in the records — the
  // server-authoritative column set.
  const seen = new Set<string>();
  for (const rec of page.records) {
    for (const key of Object.keys(rec.fields ?? {})) {
      seen.add(key);
    }
  }
  // Then, order the columns. Declared fields appear first (in
  // declaration order), then any record-only keys (in first-seen
  // order). Declared-but-absent fields are dropped (AC-4 invariant).
  const declared: string[] = (definition.definition_json?.fields ?? []).map(
    (f) => f.name,
  );
  const ordered: string[] = [];
  const emitted = new Set<string>();
  for (const name of declared) {
    if (!seen.has(name)) continue;
    ordered.push(name);
    emitted.add(name);
  }
  for (const name of seen) {
    if (emitted.has(name)) continue;
    ordered.push(name);
    emitted.add(name);
  }
  return ordered;
}

/**
 * Resolve aria-sort for a given field name. Returns
 * `"ascending" | "descending" | "none"` (WAI-ARIA 1.2).
 */
function ariaSortFor(
  field: string,
  sort: EntityListSort[],
): "ascending" | "descending" | "none" {
  const entry = sort.find((s) => s.field === field);
  if (entry === undefined) return "none";
  return entry.dir === "asc" ? "ascending" : "descending";
}

/**
 * Resolve the field label for a header cell. Falls back to the
 * field id when no label is available (definition metadata is
 * optional for the list view — INV-2 means we trust the server
 * wire shape, not the definition's display names).
 */
function fieldLabel(fieldName: string): string {
  return fieldName;
}

/**
 * Format one cell value for display. INV-9: every value renders as
 * a React text node. Null / undefined / objects → empty string.
 * Booleans → "Yes" / "No" (chrome-localised via i18n at the table
 * level — see entity_list.boolean_yes / entity_list.boolean_no).
 * Numbers / strings render verbatim.
 */
function formatCellValue(
  value: unknown,
  formatBool: (val: boolean) => string,
): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return formatBool(value);
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  // Objects / arrays — fall back to JSON. The cell text node escapes
  // any HTML-sensitive characters.
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

/**
 * EntityListTable — presentational table for the list view.
 */
export function EntityListTable({
  definition,
  page,
  sort,
  onToggleSort,
  onRowClick,
}: EntityListTableProps): React.ReactElement {
  const { t } = useTranslation();
  const columns = resolveColumns(definition, page);

  if (page.records.length === 0) {
    // AC-2 / FR-QRY-3 AC-1: empty-state chrome (NOT permission-denied
    // banner — see file header).
    return (
      <div
        className="entity-list-empty"
        data-testid="entity-list-empty"
        role="status"
        aria-live="polite"
      >
        {t("lists.empty.no_records", "No records to display.")}
      </div>
    );
  }

  return (
    <div
      className="entity-list-table-wrapper"
      data-testid="entity-list-table-wrapper"
    >
      <table
        className="entity-list-table"
        data-testid="entity-list-table"
        aria-label={t("lists.table.aria_label", "Entity records table")}
      >
        <thead>
          <tr>
            {columns.map((field) => {
              const ariaSort = ariaSortFor(field, sort);
              const sortLabel = t(
                "lists.sort.toggle_aria",
                "Toggle sort for {{field}}",
                {
                  field,
                },
              );
              return (
                <th
                  key={field}
                  scope="col"
                  className="entity-list-th"
                  data-testid={`entity-list-header-${field}`}
                  aria-sort={ariaSort}
                >
                  <button
                    type="button"
                    className="entity-list-sort-button"
                    data-testid={`entity-list-sort-${field}`}
                    onClick={() => onToggleSort(field)}
                    aria-label={sortLabel}
                  >
                    <span>{fieldLabel(field)}</span>
                    <span
                      className="entity-list-sort-indicator"
                      aria-hidden="true"
                    >
                      {ariaSort === "ascending"
                        ? t("lists.sort.asc", "▲")
                        : ariaSort === "descending"
                          ? t("lists.sort.desc", "▼")
                          : ""}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {page.records.map((record) => (
            <tr
              key={record.id}
              className="entity-list-row"
              data-testid={`entity-list-row-${record.id}`}
              onClick={
                onRowClick !== undefined
                  ? () => onRowClick(record.id)
                  : undefined
              }
              tabIndex={onRowClick !== undefined ? 0 : undefined}
              onKeyDown={
                onRowClick !== undefined
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(record.id);
                      }
                    }
                  : undefined
              }
            >
              {columns.map((field) => {
                const value = record.fields?.[field];
                return (
                  <td
                    key={field}
                    className="entity-list-td"
                    data-testid={`entity-list-cell-${record.id}-${field}`}
                  >
                    {formatCellValue(value, (b) =>
                      b
                        ? t("lists.boolean.yes", "Yes")
                        : t("lists.boolean.no", "No"),
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
